/**
 * Resolves the single DOM element a column's header filter/search box should
 * teleport into.
 *
 * DataTables' scrolling layout keeps a static clone of the `<thead>` in
 * `.dt-scroll-head` (the visible header) alongside the height-collapsed real
 * header inside `.dt-scroll-body`. Both cells carry the same
 * `${id}_header_${column}` marker class, so a bare `.${base}` selector is
 * ambiguous: `querySelector` returns whichever comes first in the DOM, and
 * across a Turbo cache-restore a second box can teleport into the other clone —
 * producing the stacked/duplicate search boxes reported in TOF-612.
 *
 * Scoping to this table's `.dt-container` and preferring the visible
 * `.dt-scroll-head` cell makes the target singular and deterministic.
 * Non-scrolling tables have no `.dt-scroll-head`, so we fall back to the lone
 * header cell, and finally to the bare selector string when the container
 * isn't resolvable yet (SafeTeleport also accepts a selector string).
 *
 * Below the `md` breakpoint an opted-in table hosts its filters in a bottom
 * sheet that lives outside `.dt-container` entirely, so a fourth, additive
 * FIRST tier resolves within a sheet host element handed in by the caller —
 * the caller owns a per-instance reference, which is a stronger scope than any
 * selector, not a weaker one. Absent a sheet host (or no match inside one),
 * resolution falls through to the three existing tiers, byte-identical.
 */
export const resolveHeaderTarget = (
  container: Element | null,
  base: string,
  sheetHost?: Element | null
): Element | string => {
  if (sheetHost) {
    const sheetTarget = sheetHost.querySelector(`.${base}`);
    if (sheetTarget) return sheetTarget;
  }

  if (!container) return `.${base}`;

  return (
    container.querySelector(`.dt-scroll-head .${base}`) ||
    container.querySelector(`.${base}`) ||
    `.${base}`
  );
};
